"use client";

/**
 * Audience-visible progress indicator — small fixed pill in the top-right
 * reading "12 / 25 · T1" (current screen / total · scenario tag). Gives
 * everyone in the room a sense of arc and reduces the "how long is this?"
 * tax on attention.
 *
 *   - Hidden on the Final Reveal screens (6.1a / 6.1b) because they're
 *     full-bleed canvas; the badge would compete with the closing line.
 *   - Hidden when no scenario is active (no currentScreen).
 *
 * Reads `currentScreen.screen` (e.g. "2.5", "4.13") from the SSE state.
 * Looks up total screen count + label from a static map keyed by the
 * leading digit of the screen ID — keeps this component dependency-free
 * (no scenario catalogue fetch).
 */

const SCENARIO_META: Record<string, { total: number; tag: string }> = {
  // T1 — acts 0..3 + bridge 3.7 = 26 total
  "0": { total: 26, tag: "T1" },
  "1": { total: 26, tag: "T1" },
  "2": { total: 26, tag: "T1" },
  "3": { total: 26, tag: "T1" },
  // T2 — act 4 (17 screens after reorder collapse + bridge 4.18 = 18)
  "4": { total: 18, tag: "T2" },
  // T3 — act 5 = 18
  "5": { total: 18, tag: "T3" },
  // Act 6 — final reveal (single screen); badge hidden anyway
  "6": { total: 1,  tag: "Act 6" },
};

function lookupForScreen(screenId: string): { total: number; tag: string; index: number } | null {
  const m = /^(\d+)\.(\d+)([a-z]?)$/.exec(screenId);
  if (!m) return null;
  const act = m[1];
  const sub = parseInt(m[2], 10);
  const meta = SCENARIO_META[act];
  if (!meta) return null;
  // Convert a `2.5` to a global index within the scenario. T1 acts run
  // 0.1-0.6 (6) + 1.1-1.6 (6) + 2.1-2.7 (7) + 3.1-3.6 (6) = 25 ordered.
  // We approximate by act-prefix offsets to keep this dep-free.
  const T1_ACT_STARTS = { "0": 0, "1": 6, "2": 12, "3": 19 } as Record<string, number>;
  if (meta.tag === "T1") {
    const offset = T1_ACT_STARTS[act] ?? 0;
    return { ...meta, index: offset + sub };
  }
  return { ...meta, index: sub };
}

export function ProgressBadge({ screen }: { screen: string | null | undefined }) {
  if (!screen) return null;
  if (screen.startsWith("6.")) return null; // hide on Final Reveal
  const info = lookupForScreen(screen);
  if (!info) return null;

  return (
    <div
      aria-label={`Screen ${info.index} of ${info.total} · ${info.tag}`}
      className="pointer-events-none fixed top-4 right-4 z-30 select-none rounded-full border border-state-verified/35 bg-orbitra-ink/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-orbitra-teal-soft shadow-[0_2px_12px_rgba(0,0,0,0.35)] backdrop-blur"
    >
      <span className="text-orbitra-paper">{info.index}</span>
      <span className="mx-0.5 text-orbitra-muted">/</span>
      <span>{info.total}</span>
      <span className="mx-1.5 text-orbitra-muted">·</span>
      <span className="text-state-verified">{info.tag}</span>
    </div>
  );
}
